import { motion, useReducedMotion } from 'framer-motion'
import { Link, useLocation } from 'react-router-dom'
import { useEffect, useState, type ReactNode } from 'react'
import { orgSchema, breadcrumbSchema } from '../lib/seo'
import SeoHead from '../components/SeoHead'
import QuoteWizard from '../components/devis/QuoteWizard'
import CalEmbed from '../components/booking/CalEmbed'
import { LINKS } from '../lib/links'
import { useT, useLocale } from '../i18n/LocaleContext'
import { localizedPath } from '../i18n/routes'

const SOCIAL = [
  {
    label: 'LinkedIn',
    href: LINKS.linkedin,
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
        <path d="M20.5 2h-17A1.5 1.5 0 002 3.5v17A1.5 1.5 0 003.5 22h17a1.5 1.5 0 001.5-1.5v-17A1.5 1.5 0 0020.5 2zM8 19H5v-9h3zM6.5 8.25A1.75 1.75 0 118.3 6.5a1.78 1.78 0 01-1.8 1.75zM19 19h-3v-4.74c0-1.42-.6-1.93-1.38-1.93A1.74 1.74 0 0013 14.19a.66.66 0 000 .14V19h-3v-9h2.9v1.3a3.11 3.11 0 012.7-1.4c1.55 0 3.36.86 3.36 3.66z" />
      </svg>
    ),
  },
  {
    label: 'Instagram',
    href: LINKS.instagram,
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="3" width="18" height="18" rx="5" />
        <circle cx="12" cy="12" r="4" />
        <circle cx="17.5" cy="6.5" r="0.7" fill="currentColor" />
      </svg>
    ),
  },
  {
    label: 'Facebook',
    href: LINKS.facebook,
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
        <path d="M22 12a10 10 0 10-11.56 9.88v-6.99H7.9V12h2.54V9.8c0-2.5 1.49-3.89 3.77-3.89 1.1 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56V12h2.77l-.44 2.89h-2.33v6.99A10 10 0 0022 12z" />
      </svg>
    ),
  },
]

type Tab = 'call' | 'quote'

/**
 * Which tab a URL fragment selects. Every CTA across the site now lands here,
 * and the fragment is how each one keeps the promise printed on its label:
 * "Réserver un échange de 30 min" must not open a four-step form, and
 * "Demander un devis" must not open a calendar.
 */
function tabFromHash(hash: string): Tab | null {
  if (hash === '#rendez-vous') return 'call'
  if (hash === '#devis') return 'quote'
  return null
}

export default function ContactPage() {
  const { t } = useT()
  const locale = useLocale()
  const reduce = useReducedMotion()
  const { hash } = useLocation()

  // Deliberately NOT seeded from the hash: the prerendered HTML is built without
  // one (a fragment never reaches the server), so seeding it would make the
  // first client render disagree with the markup React is hydrating. The effect
  // below runs immediately after hydration instead — imperceptible, and no
  // mismatch.
  const [tab, setTab] = useState<Tab>('call')

  // Nothing mounts a calendar until the fragment above has been applied.
  // Without this, landing on #devis still built a Cal.com iframe inside the
  // hidden quote panel: the first render is always 'call', and the `inline`
  // command it queues survives the teardown that follows a millisecond later
  // (the embed script drains its queue whenever it finishes loading). The
  // result was a wasted iframe fetch in a panel nobody was looking at.
  const [hydrated, setHydrated] = useState(false)

  useEffect(() => {
    setHydrated(true)
    const fromHash = tabFromHash(hash)
    if (fromHash) setTab(fromHash)
  }, [hash])

  /**
   * LAYOUT — the calendar tab needs the whole content width.
   *
   * Cal.com chooses its own layout from the width of the box it is handed: in
   * the ~520px right-hand column it stacks into a 2978px-tall booker, while at
   * ~1020px it renders the 620px three-pane view (event details, month grid,
   * time slots) — the one the event type's own preview shows. Both measured on
   * the built page at a 1440px viewport.
   */
  const wide = tab === 'call'

  const TABS: { id: Tab; label: string; hint: string; icon: ReactNode }[] = [
    {
      id: 'call',
      label: t('contactPage.tabCall'),
      hint: t('contactPage.tabCallHint'),
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden>
          <rect x="3" y="5" width="18" height="16" rx="2" />
          <path d="M3 10h18M8 3v4M16 3v4" strokeLinecap="round" />
        </svg>
      ),
    },
    {
      id: 'quote',
      label: t('contactPage.tabQuote'),
      hint: t('contactPage.tabQuoteHint'),
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden>
          <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" strokeLinejoin="round" />
          <path d="M14 3v5h5M9 13h6M9 17h4" strokeLinecap="round" />
        </svg>
      ),
    },
  ]

  return (
    <>
    <SeoHead
      title={t('seo.contactPage.title')}
      description={t('seo.contactPage.description')}
      path={localizedPath('contact', locale)}
      jsonLd={[
        { '@context': 'https://schema.org', ...orgSchema(locale) },
        {
          '@context': 'https://schema.org',
          ...breadcrumbSchema([
            { name: t('breadcrumb.home'), path: localizedPath('home', locale) },
            { name: t('breadcrumb.contact'), path: localizedPath('contact', locale) },
          ]),
        },
        {
          '@context': 'https://schema.org',
          '@type': 'ContactPage',
          inLanguage: locale,
          name: 'Contact Yaslan',
          description: 'Formulaire de contact pour Yaslan Logistique',
          url: 'https://yaslan.be/contact',
        },
      ]}
    />
    <main className="relative pt-28 sm:pt-32 pb-24 overflow-hidden isolate">
      {/* Background DA — matches About + Service pages */}
      <div aria-hidden className="absolute inset-x-0 top-0 h-[1100px] -z-10 pointer-events-none">
        <div
          className="absolute inset-x-0 -top-20 h-[600px]"
          style={{
            background:
              'radial-gradient(ellipse at top, rgba(59,93,255,0.22), rgba(59,93,255,0.06) 45%, transparent 75%)',
          }}
        />
        <div
          className="absolute inset-0 opacity-[0.28]"
          style={{
            backgroundImage: 'radial-gradient(rgba(59,93,255,0.12) 1px, transparent 1px)',
            backgroundSize: '28px 28px',
            maskImage: 'radial-gradient(ellipse at center, black 25%, transparent 75%)',
          }}
        />
        <motion.div
          aria-hidden
          animate={{ scale: [1, 1.15, 1], opacity: [0.4, 0.7, 0.4] }}
          transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute -right-40 top-[20%] w-[480px] h-[480px] rounded-full blur-3xl"
          style={{ background: 'radial-gradient(circle, rgba(59,93,255,0.18), transparent 70%)' }}
        />
        <motion.div
          aria-hidden
          animate={{ scale: [1, 1.1, 1], opacity: [0.3, 0.6, 0.3] }}
          transition={{ duration: 11, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
          className="absolute -left-40 top-[60%] w-[420px] h-[420px] rounded-full blur-3xl"
          style={{ background: 'radial-gradient(circle, rgba(59,93,255,0.14), transparent 70%)' }}
        />
      </div>

      <div className="relative max-w-6xl mx-auto px-5 sm:px-6">
        {/* Breadcrumb */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-xs sm:text-sm text-ink-500"
        >
          <Link to={localizedPath('home', locale)} className="hover:text-accent-700 transition-colors">
            {t('contactPage.breadcrumbHome')}
          </Link>
          <span className="mx-2 text-ink-300">/</span>
          <span className="text-ink-700">{t('contactPage.breadcrumbCurrent')}</span>
        </motion.div>

        {/* Badge */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.05 }}
          className="mt-6 sm:mt-8 inline-flex items-center gap-2 rounded-full border border-accent-200 bg-accent-50/80 backdrop-blur-sm px-3 py-1 text-xs text-accent-800"
        >
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full rounded-full bg-accent-500 opacity-60 animate-ping" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-accent-600" />
          </span>
          <span className="font-medium">{t('contactPage.badge')}</span>
        </motion.div>

        {/* Title */}
        <motion.h1
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
          className="mt-5 sm:mt-6 font-semibold tracking-[-0.03em] text-ink-950 text-[clamp(2.4rem,6vw,4.4rem)] leading-[1.04] max-w-4xl"
        >
          {t('contactPage.titlePart1')}{' '}
          <span className="font-semibold bg-gradient-to-r from-accent-600 via-accent-500 to-accent-700 bg-clip-text text-transparent pb-[0.1em] inline-block">
            {t('contactPage.titleHighlight')}
          </span>
          .
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.25 }}
          className="mt-5 sm:mt-6 text-base sm:text-lg text-ink-600 leading-relaxed max-w-2xl"
        >
          {t('contactPage.subtitlePart1')}{' '}
          <span className="text-ink-950 font-medium">{t('contactPage.subtitleHighlight')}</span>
          {t('contactPage.subtitlePart2')}
        </motion.p>

        {/* Scroll targets. Two zero-height anchors at the same spot: the
            fragment picks the tab, and both land the visitor on the switch
            rather than inside a panel whose height changes as Cal.com loads. */}
        <span id="rendez-vous" aria-hidden className="block scroll-mt-28" />
        <span id="devis" aria-hidden className="block scroll-mt-28" />

        {/* Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.35 }}
          role="tablist"
          aria-label={t('contactPage.badge')}
          className="mt-10 sm:mt-12 flex w-full flex-col gap-1 rounded-[1.75rem] border border-ink-200 bg-white/70 p-1.5 backdrop-blur-sm shadow-[0_18px_50px_-28px_rgba(14,14,20,0.55)] sm:inline-flex sm:w-auto sm:flex-row sm:items-center"
        >
          {TABS.map((item) => {
            const selected = tab === item.id
            return (
              <button
                key={item.id}
                type="button"
                role="tab"
                id={`contact-tab-${item.id}`}
                aria-selected={selected}
                aria-controls={`contact-panel-${item.id}`}
                onClick={() => setTab(item.id)}
                className="relative w-full rounded-[1.4rem] px-4 sm:px-5 py-2.5 text-left transition-colors sm:w-auto"
              >
                {selected && (
                  <motion.span
                    aria-hidden
                    layoutId="contact-tab-pill"
                    transition={reduce ? { duration: 0 } : { type: 'spring', stiffness: 420, damping: 34 }}
                    className="absolute inset-0 rounded-[1.4rem] bg-gradient-to-br from-accent-600 to-accent-700 shadow-[0_14px_34px_-12px_rgba(36,47,214,0.9)]"
                  />
                )}
                <span className={`relative flex items-center gap-2.5 ${selected ? 'text-white' : 'text-ink-600'}`}>
                  <span className={selected ? 'text-white' : 'text-accent-700'}>{item.icon}</span>
                  <span className="flex flex-col leading-tight">
                    <span className="text-sm font-semibold">{item.label}</span>
                    <span className={`text-[11px] ${selected ? 'text-white/70' : 'text-ink-400'}`}>{item.hint}</span>
                  </span>
                </span>
              </button>
            )
          })}
        </motion.div>

        {/* Main content grid — see LAYOUT note above `wide` */}
        <div
          className={
            wide
              ? 'mt-8 sm:mt-10 flex flex-col gap-8'
              : 'mt-8 sm:mt-10 grid lg:grid-cols-[0.85fr_1.15fr] gap-8 lg:gap-12'
          }
        >
          {/* ───────────── INFO COLUMN ───────────── */}
          <div
            className={
              wide
                ? 'order-2 grid gap-4 sm:grid-cols-2 lg:grid-cols-4'
                : 'space-y-4 lg:sticky lg:top-32 lg:self-start'
            }
          >
            {/* Response promise */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="relative rounded-2xl border border-accent-200 bg-gradient-to-br from-accent-50 via-white to-accent-50/60 p-5 sm:p-6 overflow-hidden"
            >
              <motion.div
                aria-hidden
                animate={{ scale: [1, 1.2, 1], opacity: [0.4, 0.7, 0.4] }}
                transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
                className="absolute -right-10 -top-10 w-32 h-32 rounded-full blur-2xl"
                style={{ background: 'radial-gradient(circle, rgba(59,93,255,0.3), transparent 70%)' }}
              />
              <div className="relative flex items-start gap-3">
                <div className="relative flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-accent-600 to-accent-700 text-white shadow-[0_10px_24px_-10px_rgba(59,93,255,0.7)] shrink-0">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
                    <circle cx="12" cy="12" r="9" />
                    <path d="M12 7v5l3 2" strokeLinecap="round" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-semibold text-ink-950">{t('contactPage.responseTitle')}</p>
                  <p className="mt-1 text-xs text-ink-600 leading-relaxed">
                    {t('contactPage.responseDesc')}
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Email */}
            <InfoCard
              delay={0.08}
              icon={
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                  <rect x="3" y="5" width="18" height="14" rx="2" />
                  <path d="M3 7l9 6 9-6" strokeLinecap="round" />
                </svg>
              }
              label={t('contactPage.emailLabel')}
            >
              <a
                href="mailto:contact@yaslan.be"
                className="font-medium text-ink-950 hover:text-accent-700 transition-colors"
              >
                contact@yaslan.be
              </a>
            </InfoCard>

            {/* Localisation */}
            <InfoCard
              delay={0.16}
              icon={
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                  <path d="M12 21s-7-7.5-7-13a7 7 0 1 1 14 0c0 5.5-7 13-7 13z" strokeLinejoin="round" />
                  <circle cx="12" cy="8" r="2.5" />
                </svg>
              }
              label={t('contactPage.addressLabel')}
            >
              <p className="font-medium text-ink-950 leading-snug">
                {t('contactPage.addressLine')}
              </p>
              <p className="text-xs text-ink-500 mt-0.5">{t('contactPage.addressCountry')}</p>
            </InfoCard>

            {/* Social */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.7, delay: 0.24, ease: [0.22, 1, 0.36, 1] }}
              className="relative rounded-2xl border border-ink-100 bg-ink-100 p-5 sm:p-6"
            >
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-[10px] font-semibold tracking-[0.2em] uppercase text-ink-500">
                    {t('contactPage.followLabel')}
                  </p>
                  <p className="text-xs text-ink-600 mt-1">{t('contactPage.followDesc')}</p>
                </div>
                <div className="flex items-center gap-2">
                  {SOCIAL.map((s) => (
                    <a
                      key={s.label}
                      href={s.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={s.label}
                      className="w-9 h-9 rounded-full border border-ink-200 text-ink-500 hover:text-accent-700 hover:border-accent-300 hover:bg-accent-50 flex items-center justify-center transition-colors"
                    >
                      {s.icon}
                    </a>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>

          {/* ───────────── FORM COLUMN ───────────── */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
            className={`relative rounded-2xl sm:rounded-3xl border border-ink-100 bg-ink-100 p-6 sm:p-10 shadow-[0_30px_80px_-30px_rgba(14,14,20,0.18)] overflow-hidden ${
              wide ? 'order-1' : ''
            }`}
          >
            <motion.div
              aria-hidden
              animate={{ opacity: [0.3, 0.6, 0.3] }}
              transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
              className="pointer-events-none absolute -right-32 -top-32 w-72 h-72 rounded-full blur-3xl"
              style={{ background: 'radial-gradient(circle, rgba(59,93,255,0.18), transparent 70%)' }}
            />

            {/* Both panels stay mounted so switching tabs never wipes the
                wizard's answers; only the calendar is torn down when hidden. */}
            <div
              role="tabpanel"
              id="contact-panel-call"
              aria-labelledby="contact-tab-call"
              hidden={tab !== 'call'}
            >
              <p className="text-[10px] font-semibold tracking-[0.2em] uppercase text-accent-700">
                {t('contactPage.tabCallHint')}
              </p>
              <h2 className="mt-2 text-xl sm:text-2xl font-semibold tracking-tight text-ink-950">
                {t('contactPage.callTitle')}
              </h2>
              <p className="mt-2 max-w-lg text-sm leading-relaxed text-ink-600">
                {t('contactPage.callDesc')}
              </p>
              <div className="mt-6">
                <CalEmbed
                  namespace="contact"
                  active={hydrated && tab === 'call'}
                  loadingLabel={t('contactPage.callLoading')}
                  fallbackLabel={t('contactPage.callFallback')}
                />
              </div>
            </div>

            <div
              role="tabpanel"
              id="contact-panel-quote"
              aria-labelledby="contact-tab-quote"
              hidden={tab !== 'quote'}
            >
              <QuoteWizard active={hydrated && tab === 'quote'} />
            </div>
          </motion.div>
        </div>
      </div>
    </main>
    </>
  )
}

// ─────────────── helpers ───────────────

function InfoCard({
  icon,
  label,
  children,
  delay,
}: {
  icon: ReactNode
  label: string
  children: ReactNode
  delay: number
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
      className="relative rounded-2xl border border-ink-100 bg-ink-100 p-5 sm:p-6 hover:border-accent-200 hover:shadow-[0_20px_50px_-22px_rgba(59,93,255,0.4)] transition-all"
    >
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-xl bg-accent-50 text-accent-700 border border-accent-100 flex items-center justify-center shrink-0">
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-semibold tracking-[0.2em] uppercase text-ink-500">
            {label}
          </p>
          <div className="mt-1 text-sm">{children}</div>
        </div>
      </div>
    </motion.div>
  )
}
